import { useInboxItemsInfinite } from "@/api/inbox";
import { InboxItem, InboxItemsGetParams } from "@cohesive/api-client";
import { useMemo } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import {
  IntersectionObserverHookRefCallback as UseInfiniteScrollHookRefCallback,
  IntersectionObserverHookRootRefCallback as UseInfiniteScrollHookRootRefCallback,
} from "react-intersection-observer-hook";
import { FilterType } from "../types";

interface UseInboxDataProps {
  searchQuery: string;
  activeFilter: FilterType;
}

interface UseInboxDataInfiniteResult {
  displayItems: InboxItem[];
  priorityItems: InboxItem[];
  allItems: InboxItem[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  sentryRef: UseInfiniteScrollHookRefCallback;
  rootRef: UseInfiniteScrollHookRootRefCallback;
}

/**
 * Converts FilterType to API query parameters
 */
function createApiFiltersFromFilterType(
  activeFilter: FilterType,
  searchQuery: string,
): InboxItemsGetParams {
  const params: InboxItemsGetParams = {};

  // Add search query if present
  if (searchQuery.trim()) {
    params.search_term = searchQuery.trim();
  }

  // Set filter-specific parameters
  switch (activeFilter) {
    case FilterType.CHATS:
      params.resource_type = "flow-template";
      params.is_archived = false;
      break;
    case FilterType.STARRED:
      params.is_starred = true;
      break;
    case FilterType.ARCHIVED:
      params.is_archived = true;
      break;
    case FilterType.ALL:
    default:
      params.is_archived = false;
      break;
  }

  return params;
}

/**
 * Custom hook to manage inbox data fetching with infinite scroll and API-based filtering
 */
export function useInboxDataInfinite({
  searchQuery,
  activeFilter,
}: UseInboxDataProps): UseInboxDataInfiniteResult {
  // Create API parameters based on current filter
  const apiParams = useMemo(() => {
    return createApiFiltersFromFilterType(activeFilter, searchQuery);
  }, [activeFilter, searchQuery]);

  // Single infinite query with API-based filtering
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInboxItemsInfinite(apiParams);

  // Flatten all pages into a single array
  const displayItems = useMemo(() => {
    return data?.pages.flat() || [];
  }, [data]);

  // Separate priority and regular items for UI organization
  // Note: API returns items sorted by creation date, so we maintain that order
  const { priorityItems, allItems } = useMemo(() => {
    const priority = displayItems.filter(
      (item) => item.has_pending_tool_confirmations,
    );
    const regular = displayItems.filter(
      (item) => !item.has_pending_tool_confirmations,
    );

    return {
      priorityItems: priority,
      allItems: regular,
    };
  }, [displayItems]);

  // Infinite scroll hook
  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
    rootMargin: "0px 0px 1000px 0px",
  });

  return {
    displayItems,
    priorityItems,
    allItems,
    isLoading,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    sentryRef,
    rootRef,
  };
}
